import { Component } from '@theme/component';

/**
 * Client-side category/author filtering and sort for the blog archive page.
 * There is no Storefront facets API for blog articles, so the section also
 * renders the other pagination pages' articles as hidden `data-offpage` cards.
 * With no filter active only the current page's cards show; while a filter is
 * active every matching card (any page) shows and pagination is hidden.
 *
 * Desktop renders an accordion filter bar that applies each change instantly.
 * Below 991px the accordion is hidden in favor of a sticky trigger button
 * that opens a `<theme-drawer>` bottom sheet; selections made there are
 * staged (pending) and only take effect on "Apply" — closing the drawer any
 * other way (backdrop, Escape, the close button) discards them.
 *
 * The mobile drawer's contents live inside `<theme-drawer>`, which is itself
 * a `Component` — any `ref` inside it is claimed by the drawer's own
 * instance, not this one, so those elements are looked up with plain
 * `querySelectorAll` instead. Their `on:click` handlers likewise specify the
 * `blog-archive-component` selector explicitly rather than relying on
 * closest-component resolution, which would otherwise stop at the drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLElement} [gridWrapper]
 * @property {HTMLElement[]} [cards]
 * @property {HTMLElement} [categoryLabel]
 * @property {HTMLElement} [authorLabel]
 * @property {HTMLElement[]} [categoryButtons]
 * @property {HTMLElement[]} [authorButtons]
 * @property {HTMLElement[]} [sortButtons]
 * @property {import('@theme/theme-drawer').ThemeDrawer} [filterDrawer]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [pagination]
 *
 * @extends {Component<Refs>}
 */
class BlogArchiveComponent extends Component {
  requiredRefs = ['grid'];

  /** How long the loading indicator stays visible while a filter/sort is applied. */
  static LOADING_DURATION = 500;

  /** @type {Set<string>} Currently applied category filters. */
  #categories = new Set();

  /** @type {Set<string>} Currently applied author filters. */
  #authors = new Set();

  /** @type {string} Currently applied sort value. */
  #sortValue = 'newest';

  /** @type {Set<string>} Category selections staged in the mobile drawer, not yet applied. */
  #pendingCategories = new Set();

  /** @type {Set<string>} Author selections staged in the mobile drawer, not yet applied. */
  #pendingAuthors = new Set();

  /** @type {string} Sort value staged in the mobile drawer, not yet applied. */
  #pendingSortValue = 'newest';

  /** @type {number} Number of in-flight loading animations, so overlapping calls don't clear the indicator early. */
  #loadingCount = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('toggle', this.#handleToggle, true);
    document.addEventListener('click', this.#handleOutsideClick);
    this.refs.filterDrawer?.addEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('toggle', this.#handleToggle, true);
    document.removeEventListener('click', this.#handleOutsideClick);
    this.refs.filterDrawer?.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }

  /** @param {Event} event */
  toggleCategory(event) {
    this.#toggleValue(event, this.#categories, this.refs.categoryLabel);
    this.#runWithLoading(() => this.#applyFilters());
  }

  /** @param {Event} event */
  toggleAuthor(event) {
    this.#toggleValue(event, this.#authors, this.refs.authorLabel);
    this.#runWithLoading(() => this.#applyFilters());
  }

  /** @param {Event} event */
  setSort(event) {
    const button = this.#getButton(event);
    if (!button) return;

    this.#setActiveButton(button);

    const details = button.closest('details');
    if (details instanceof HTMLDetailsElement) details.open = false;

    this.#sortValue = button.dataset.value ?? 'newest';
    this.#runWithLoading(() => this.#sortGrid(this.#sortValue));
  }

  /** @param {Event} event */
  toggleMobileCategory(event) {
    this.#togglePendingValue(event, this.#pendingCategories);
  }

  /** @param {Event} event */
  toggleMobileAuthor(event) {
    this.#togglePendingValue(event, this.#pendingAuthors);
  }

  /** @param {Event} event */
  setMobileSort(event) {
    const button = this.#getButton(event);
    if (!button) return;

    this.#setActiveButton(button);
    this.#pendingSortValue = button.dataset.value ?? 'newest';
    this.#updateFilterBadge();
  }

  /** Commits the drawer's staged selections and filters/sorts the grid. */
  applyMobileFilters() {
    this.#categories = new Set(this.#pendingCategories);
    this.#authors = new Set(this.#pendingAuthors);
    this.#sortValue = this.#pendingSortValue;

    this.#syncDesktopButtons();
    this.#runWithLoading(() => {
      this.#applyFilters();
      this.#sortGrid(this.#sortValue);
    });

    this.refs.filterDrawer?.close();
  }

  /** Resets the drawer's staged selections without touching the applied filters/grid. */
  clearMobileFilters() {
    this.#pendingCategories.clear();
    this.#pendingAuthors.clear();
    this.#pendingSortValue = 'newest';
    this.#syncMobileButtons();
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getButton(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-value]') : null;
    return button instanceof HTMLElement ? button : null;
  }

  /**
   * Marks `button` active and deactivates its siblings (single-select group, e.g. sort options).
   *
   * @param {HTMLElement} button
   */
  #setActiveButton(button) {
    const options = button.parentElement ? Array.from(button.parentElement.children) : [];
    for (const option of options) option.classList.toggle('is-active', option === button);
  }

  /**
   * @param {Event} event
   * @param {Set<string>} set
   * @param {HTMLElement | undefined} labelEl
   */
  #toggleValue(event, set, labelEl) {
    const button = this.#getButton(event);
    if (!button) return;

    const value = button.dataset.value ?? '';
    const active = button.classList.toggle('is-active');

    if (active) {
      set.add(value);
    } else {
      set.delete(value);
    }

    this.#updateLabel(labelEl, set.size);
  }

  /**
   * @param {Event} event
   * @param {Set<string>} set
   */
  #togglePendingValue(event, set) {
    const button = this.#getButton(event);
    if (!button) return;

    const value = button.dataset.value ?? '';
    const active = button.classList.toggle('is-active');

    if (active) {
      set.add(value);
    } else {
      set.delete(value);
    }

    this.#updateFilterBadge();
  }

  /**
   * @param {HTMLElement | undefined} labelEl
   * @param {number} count
   */
  #updateLabel(labelEl, count) {
    if (!labelEl) return;
    const base = labelEl.dataset.label ?? labelEl.textContent ?? '';
    labelEl.textContent = count ? `${base} (${count})` : base;
  }

  #applyFilters() {
    const cards = this.refs.cards ?? [];
    const filtering = this.#categories.size > 0 || this.#authors.size > 0;
    let visibleCount = 0;

    for (const card of cards) {
      const cardCategories = (card.dataset.categories ?? '').split('|').filter(Boolean);
      const cardAuthor = card.dataset.author ?? '';

      const matchesCategory = this.#categories.size === 0 || cardCategories.some((value) => this.#categories.has(value));
      const matchesAuthor = this.#authors.size === 0 || this.#authors.has(cardAuthor);
      const onCurrentPage = !('offpage' in card.dataset);
      const visible = matchesCategory && matchesAuthor && (filtering || onCurrentPage);

      card.hidden = !visible;
      if (visible) visibleCount += 1;
    }

    if (this.refs.empty) this.refs.empty.hidden = visibleCount !== 0;
    if (this.refs.pagination) {
      this.refs.pagination.hidden = filtering;
    }
  }

  /** @param {string} value */
  #sortGrid(value) {
    const { grid, cards } = this.refs;
    if (!grid || !cards) return;

    const sorted = [...cards].sort((a, b) => {
      switch (value) {
        case 'oldest':
          return Number(a.dataset.date) - Number(b.dataset.date);
        case 'title-asc':
          return (a.dataset.title ?? '').localeCompare(b.dataset.title ?? '');
        case 'title-desc':
          return (b.dataset.title ?? '').localeCompare(a.dataset.title ?? '');
        case 'newest':
        default:
          return Number(b.dataset.date) - Number(a.dataset.date);
      }
    });

    for (const card of sorted) grid.append(card);
  }

  /**
   * Shows the loading indicator, waits out the animation, then runs `fn` and hides it again.
   * Tracks overlapping calls (rapid clicks) so the indicator isn't hidden prematurely.
   *
   * @param {() => void} fn
   */
  async #runWithLoading(fn) {
    const wrapper = this.refs.gridWrapper;
    this.#loadingCount += 1;
    wrapper?.classList.add('is-filtering');

    await new Promise((resolve) => setTimeout(resolve, BlogArchiveComponent.LOADING_DURATION));

    fn();

    this.#loadingCount = Math.max(0, this.#loadingCount - 1);
    if (this.#loadingCount === 0) wrapper?.classList.remove('is-filtering');
  }

  /** Stages the drawer's selections from the currently applied filters when it opens. */
  #handleDrawerOpen = () => {
    this.#pendingCategories = new Set(this.#categories);
    this.#pendingAuthors = new Set(this.#authors);
    this.#pendingSortValue = this.#sortValue;
    this.#syncMobileButtons();
  };

  #syncMobileButtons() {
    for (const button of this.#mobileButtons('category')) {
      button.classList.toggle('is-active', this.#pendingCategories.has(button.dataset.value ?? ''));
    }
    for (const button of this.#mobileButtons('author')) {
      button.classList.toggle('is-active', this.#pendingAuthors.has(button.dataset.value ?? ''));
    }
    for (const button of this.#mobileButtons('sort')) {
      button.classList.toggle('is-active', button.dataset.value === this.#pendingSortValue);
    }
    this.#updateFilterBadge();
  }

  /**
   * Buttons inside the mobile drawer aren't reachable via `this.refs` (see class doc), so they're
   * looked up directly by their `data-mobile-filter` marker instead.
   *
   * @param {'category' | 'author' | 'sort'} group
   * @returns {HTMLElement[]}
   */
  #mobileButtons(group) {
    return Array.from(this.querySelectorAll(`[data-mobile-filter="${group}"]`));
  }

  /** Mirrors the applied filters back onto the desktop accordion buttons after a mobile Apply. */
  #syncDesktopButtons() {
    for (const button of this.refs.categoryButtons ?? []) {
      button.classList.toggle('is-active', this.#categories.has(button.dataset.value ?? ''));
    }
    for (const button of this.refs.authorButtons ?? []) {
      button.classList.toggle('is-active', this.#authors.has(button.dataset.value ?? ''));
    }
    for (const button of this.refs.sortButtons ?? []) {
      button.classList.toggle('is-active', button.dataset.value === this.#sortValue);
    }
    this.#updateLabel(this.refs.categoryLabel, this.#categories.size);
    this.#updateLabel(this.refs.authorLabel, this.#authors.size);
  }

  /** Count shown in the drawer header badge; sort always has one active value, so it always counts. */
  #updateFilterBadge() {
    const badge = this.querySelector('[data-mobile-filter="badge"]');
    if (!badge) return;
    badge.textContent = String(this.#pendingCategories.size + this.#pendingAuthors.size + 1);
  }

  /** @param {Event} event */
  #handleToggle = (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;

    for (const details of this.querySelectorAll('details')) {
      if (details !== opened) details.open = false;
    }
  };

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    for (const details of this.querySelectorAll('details[open]')) {
      details.open = false;
    }
  };
}

if (!customElements.get('blog-archive-component')) {
  customElements.define('blog-archive-component', BlogArchiveComponent);
}
